import dayjs from 'dayjs';
import type { UndefinedOnPartialDeep } from 'type-fest';
import { useShallow } from 'zustand/shallow';

import { useTimeWindowUpdateStore } from '@/store/time-window-update.store';
import type { Service } from '@repo/types/service';

import { getServiceTimeWindowStartUtc } from '@/utils/services.utils';

import { usePendingTimeWindows } from './pending-time-windows.hooks';
import { useTimeWindowCandidates } from './time-window-candidates.hooks';

interface UseTimeWindowFooterParams {
    service: UndefinedOnPartialDeep<Service>;
    onSuccess?: (() => void) | undefined;
}

export const useTimeWindowFooter = ({ service, onSuccess }: UseTimeWindowFooterParams) => {
    const { selectedDayKey, selectedTimeWindowId, selectedReasonCode } = useTimeWindowUpdateStore(
        useShallow((state) => ({
            selectedDayKey: state.selectedDayKey,
            selectedTimeWindowId: state.selectedTimeWindowId,
            selectedReasonCode: state.selectedReasonCode,
        }))
    );
    const currentTimeWindowId = service.timeWindow?.timeWindowId;
    const activeMonthStartDate = selectedDayKey
        ? dayjs.utc(selectedDayKey).startOf('month')
        : getServiceTimeWindowStartUtc(service).startOf('month');
    const timeWindowCandidates = useTimeWindowCandidates({
        service,
        activeMonthStartDate,
        selectedDayKey,
        selectedTimeWindowId,
    });
    const { isSelectedTimeWindowInvalid } = timeWindowCandidates;
    const isDisabled =
        selectedTimeWindowId === undefined ||
        selectedTimeWindowId === currentTimeWindowId ||
        isSelectedTimeWindowInvalid ||
        !selectedReasonCode;
    const { applyTimeWindowChange, isAssetDetailsEditMode, isPending } = usePendingTimeWindows({
        service,
        timeWindowCandidates,
        reasonCode: selectedReasonCode,
        onSuccess,
    });

    const handleClick = () => {
        if (selectedTimeWindowId) {
            void applyTimeWindowChange(selectedTimeWindowId);
        }
    };

    return {
        isDisabled: isDisabled || !isAssetDetailsEditMode,
        isPending,
        handleClick,
    };
};
